import { Injectable } from '@angular/core';
import { SnackBarService } from '@my7n/ui';

import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import * as ngrxUtils from '../../utils/ngrx-utils';

import * as GalleriesActions from '../actions/galleries.actions';
import * as fromGalleries from '../reducers/galleries';
import * as GalleriesSelectors from '../reducers/galleries/galleries.selectors';

import { ISharepointFiles } from '../../interfaces/sharepoint';
import { SharepointService } from '../../services/sharepoint.service';

@Injectable()
export class GalleriesEffects {
  constructor(
    private actions$: Actions,
    private snackBarService: SnackBarService,
    private galleriesStore$: Store<fromGalleries.State>,
    private sharepointService: SharepointService
  ) {}

  queryGallery$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GalleriesActions.queryGalleryData),
      distinctUntilChanged(),
      concatLatestFrom((action) =>
        this.galleriesStore$.select(GalleriesSelectors.selectGallery, { path: action.path })
      ),
      mergeMap(([action, gallery]) => {
        const requestedLimit = action.galleryParams.limit ?? null;
        const cachedLimit = gallery?.loadedWithLimit ?? null;
        const isSameLimitRequest = cachedLimit === requestedLimit;
        const isLimitedRequest = requestedLimit !== null;
        const canUseFullCacheForLimitedRequest = cachedLimit === null && isLimitedRequest;

        if (
          gallery?.FilesData &&
          !action.forceRefresh &&
          (isSameLimitRequest || canUseFullCacheForLimitedRequest) &&
          ngrxUtils.isInCacheLimit(gallery.lastRequestTimestamp, 600)
        ) {
          return of(GalleriesActions.galleryLoadedFromCache({ path: action.path }));
        } else {
          return this.sharepointService.getGalleryFiles(action.path, action.galleryParams).pipe(
            map((res: ISharepointFiles) => {
              return GalleriesActions.updateGalleryData({
                gallery: {
                  FilesData: res.FilesData,
                  Count: res.Count,
                  loading: false,
                  error: false,
                  loadedWithLimit: requestedLimit,
                  basePath: action.path,
                },
              });
            }),
            catchError((err) => {
              return of(GalleriesActions.queryGalleryDataError({ path: action.path }));
            })
          );
        }
      })
    );
  });
}
